import React, { useEffect } from 'react';
import { ChannelOptionButton as UIChannelOptionButton } from '@shopee/chatbot-ui';
import { ChannelBtnType } from '@shopee/chatbot-ui/lib/components/ChannelOptionsCard/ChannelOptionButtonsPopup';
import { inject, observer } from 'mobx-react';
import { getChildInject, bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { AgentStore } from '@/stores/agent';
import initStore, { InitStore } from '@/stores/init';
import modalStore from '@/stores/modal';
import { getConfig } from '@/config/core';
import {
  INPUT_TYPE,
  LIVE_OFFLINE_FORM_PAGE,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_TYPE,
  VIRTUAL_CALL_NEED_RECOVER_HISTORY,
  YOU_HAVE_ENDED_VIRTUAL_CALL,
} from '@/utils/constants';
import { handleRequest } from '@/utils';
import { getParamsForWebFormSubmissionCheck, getTrans, redirectionTo, saveRecoveryContext, clearRecoveryData, saveChatbotAndAgentData } from '@/utils/tools';
import { ChatbotUIBasicProps, DIALOGUE_TYPE } from '@/types/common';
import { addDialogue, disableDialogueChannelOptionButton } from '@/utils/sessionUtils';
import uploadMessageToSave from '@/utils/uploadMessageToSave';
import { handleRedirectionBtnClick } from '@/utils/btnClickHandler';
import { ENTRY_POINT } from '@/types/entryPoints';
import { chatbotTrackObj } from '@/utils/tracking';
import { saveDataToLocalStorage } from '@/utils/bridges/localStorage';
import { TransferType, reportTransferType } from '@/utils/reportTransferType';
import chatbotCommonVariable from '@/utils/common';

export interface ChannelOptionButtonValues {
  button_id: string;
  button_type: string;
  button_text: string;
  extra_param?: any;
}

interface LiveRelativeProps {
  agentStore: AgentStore;
  initStore: InitStore;
  isDisabled: boolean;
  disabledBtn: boolean;
}

export type ChannelOptionButtonProps = {
  buttons: ChannelOptionButtonValues[];
  trackingname: string;

  disabledBtn: boolean;
  userStore?: any;
};

const ChannelOptionButton: React.FC<ChannelOptionButtonProps> = (props) => {
  const {
    dialogueId,
    dialogueType,
    agentStore,
    initStore,
    isDisabled,
    disabledBtn,
    isHistory,
    historySessionId,
    historyDialogueId,
    trackingname,
    userStore,
    customCallback = {},
  } = props as unknown as ChannelOptionButtonProps & LiveRelativeProps & ChatbotUIBasicProps;
  const { loadingInAgent, inAgent } = agentStore;

  const btnText = getTrans('other_options_btn_text');

  useEffect(() => {
    if (chatbotCommonVariable?.virtualCallNeedRecoverHistory) {
      handleVirtualCallListen({
        businessId: initStore?.sessionId,
        dialogueId,
        dialogueType,
        customCallback,
      });
    }
  }, []);

  return loadingInAgent || inAgent || isDisabled ? null : (
    <UIChannelOptionButton
      disabled={disabledBtn}
      optionButtonText={btnText}
      buttons={props.buttons.map((b) => ({
        ...b,
        onClick: () => {
          if (b.button_type === ChannelBtnType.BtnSubmitWebform) {
            handleSubmitWebform(b.button_text, { dialogueId, dialogueType, sessionId: initStore.sessionId }, customCallback);
          }
          if (b.button_type === ChannelBtnType.BtnGeneralCall) {
            handlePhoneCall(b.button_text, {
              dialogueId,
              dialogueType,
              sessionId: initStore.sessionId,
              phoneNumber: b.extra_param.phone_number,
            });
          }
          if (b.button_type === ChannelBtnType.BtnVirtualCall) { // 类型是 virtual call 就调用 virtual call
            userStore?.UPDATE_IS_CLICK_VIRTUAL_CALL?.(true); // 标识 拨打了 virtual call
            handleVirtualCall(
              b.button_text,
              {
                dialogueId,
                dialogueType,
                entryPoint: ENTRY_POINT[initStore?.from as any] ?? ENTRY_POINT.general,
                // entryPoint: '4', // 联调先写死
                businessId: initStore?.sessionId,
                userType: agentStore?.userType,
                enquiryType: agentStore?.enquiryType,
                orderSn: agentStore?.inboundOrderSn,
                reasonCode: agentStore?.reasonCodeL1Id,
                userTypeLabel: agentStore?.userTypeLabel,
              },
              customCallback,
            );
          }
        },
        text: b.button_text,
        buttonType: b.button_type as unknown as ChannelBtnType,
        extraParam: b.extra_param,
        trackingData: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.LIVE_AGENT_CHANNEL_SELECTION],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.LIVE_AGENT_CHANNEL_SELECTION],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : initStore.sessionId || '',
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            button_text: b.button_text,
          },
          trackingdataTarget: { button_text: b.button_text },
        },
      }))}
      trackingData={{
        trackingsection: (TRACKING_SECTION as any)[trackingname],
        trackingtarget: (TRACKING_TARGET as any)[trackingname],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : initStore.sessionId || '',
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          button_text: btnText,
        },
        trackingdataTarget: { button_text: btnText },
      }}
    />
  );
};

export function handleSubmitWebform(
  buttonText: string,
  extraParams: { dialogueId: string; dialogueType: string; sessionId: string },
  customCallback: ChatbotUIBasicProps['customCallback'],
  notDialogue?: boolean,
) {
  const { dialogueId, dialogueType, sessionId } = extraParams;
  !notDialogue && addDialogue({
    dialogueId,
    dialogueType,
    from: 'user',
    type: 'Msg',
    data: { content: buttonText },
  });

  uploadMessageToSave({
    dialogueId,
    messages: [
      {
        message_text: buttonText,
        is_sent_by_user: true,
      },
    ],
  });

  const { webFormConfig } = getConfig();
  const webFormKey = webFormConfig.webFormKey || LIVE_OFFLINE_FORM_PAGE;

  handleRedirectionBtnClick({ uri_key: webFormKey, text_key: '', text_value: '' }, undefined, () => {
    handleRequest('getWebformSubmission', getParamsForWebFormSubmissionCheck()).then((res: any) => {
      if (res && !(res instanceof Error) && res?.is_submission_success) {
        disableDialogueChannelOptionButton();

        handleRequest('postMessage', {
          session_id: sessionId,
          dialogue_id: dialogueId,
          input_type: INPUT_TYPE.BUTTON,
          message_text: buttonText,
          is_bypass: true,
          message_extra: {
            params: {
              user_send: {
                type: 'SUBMIT_WEBFORM_SUCC',
                send_scene: 'SUBMIT_WEBFORM',
              },
            },
          },
          ...customCallback,
        });
      }
    });
  });

  reportTransferType({ sessionId, transferType: TransferType.ToWebform });
}

export function handlePhoneCall(
  buttonText: string,
  extraParams: { dialogueId: string; dialogueType: string; sessionId: string; phoneNumber: string },
  notDialogue?: boolean,
) {
  const { dialogueId, dialogueType, phoneNumber, sessionId } = extraParams;
  !notDialogue && addDialogue({
    dialogueId,
    dialogueType,
    from: 'user',
    type: 'Msg',
    data: { content: buttonText },
  });

  uploadMessageToSave({
    dialogueId,
    messages: [
      {
        message_text: buttonText,
        is_sent_by_user: true,
      },
    ],
  });

  window.location.href = `tel:${phoneNumber}`;
  reportTransferType({ sessionId, transferType: TransferType.ToCall });
}

interface VirtualCallParams {
  dialogueId: string;
  dialogueType: string;
  entryPoint: string;
  businessId?: string;
  userType?: string | number;
  enquiryType?: string | number;
  orderSn?: string;
  reasonCode?: string | number;
  userTypeLabel?: string;
}

export function handleVirtualCall( // 调用 virtual call
  buttonText: string,
  extraParams: VirtualCallParams,
  customCallback: ChatbotUIBasicProps['customCallback'],
  notDialogue?: boolean,
) {
  // 如果 app 版本不对 则让用户去更新
  if (!initStore?.initSessionChatConfig?.canUseInternetCall) {
    modalStore.UPDATE_SHOW_MODAL_UPDATE_APP(true);
    return;
  }

  const { dialogueId, dialogueType, entryPoint, businessId, userType = '', enquiryType = '', orderSn = '', reasonCode = '', userTypeLabel = '' } = extraParams;
  !notDialogue && addDialogue({
    dialogueId,
    dialogueType,
    from: 'user',
    type: 'Msg',
    data: { content: buttonText },
  });

  uploadMessageToSave({
    dialogueId,
    messages: [
      {
        message_text: buttonText,
        is_sent_by_user: true,
      },
    ],
  });
  // 开发使用
  // saveChatbotAndAgentData();
  // saveDataToLocalStorage(VIRTUAL_CALL_NEED_RECOVER_HISTORY, 'true');

  redirectionTo('virtual_call_page', {
    entryPoint,
    businessId,
    userType,
    enquiryType,
    orderSn,
    reasonCode: reasonCode + '',
    userTypeLabel,
    __anim__: 3,
  }); // 调用  virtual call
  // 置灰按钮
  disableDialogueChannelOptionButton();

  handleVirtualCallListen({
    businessId,
    dialogueId,
    dialogueType,
    customCallback,
  });

  reportTransferType({ sessionId: businessId ?? '', transferType: TransferType.ToCall });
}
let bridgeCallback: (res: { data: string; }) => Promise<void> = async (res: { data: string; }) => {};
let virtualCallListenLock = false;

const handleVirtualCallListen = ({
  businessId,
  dialogueId,
  dialogueType,
  customCallback,
} : {
  businessId?: string;
  dialogueId: string;
  dialogueType: string;
  customCallback: ChatbotUIBasicProps['customCallback'];
}) => {
  if (virtualCallListenLock) {
    return;
  }

  virtualCallListenLock = true;
  bridgeCallFunc(() => { // 监听 virtual call 页面
    window.bridgeUnRegisterHandler('viewWillReappear', bridgeCallback);
    bridgeCallback = async (res: { data: string }) => {
      const checkData = await handleRequest('virtualCallStatusCheck', {
        // 检查 session 和 vc 状态
        session_id: businessId, // 这个就是 session id
      });
      const { session_expired, vc_in_progress } = checkData ?? {};
      if (session_expired) { // session id 过期就退出当前界面
        chatbotTrackObj.flush();
        clearRecoveryData(); // session 过期就清除缓存
        // saveDataToLocalStorage(VIRTUAL_CALL_NEED_RECOVER_HISTORY, 'false');
        chatbotCommonVariable?.UPDATE_VIRTUAL_CALL_NEED_RECOVER_HISTORY(false);
        window.bridgeUnRegisterHandler('viewWillReappear', bridgeCallback); // 取消 virtual call 页面 监听
        window.bridgeCallHandler('popWebView', {}, function () { }); // 退出出当前页面
      } else {
        if (!vc_in_progress) { // 如果结束 通话 就调用 message 接口
          clearRecoveryData(); // 结束通话就清除缓存
          // 插入文案
          addDialogue({
            dialogueId,
            dialogueType,
            from: 'system',
            type: DIALOGUE_TYPE.SystemText,
            // 'You have ended the call'
            data: { text: getTrans(YOU_HAVE_ENDED_VIRTUAL_CALL) }
          });
          await handleRequest('postMessage', {
            session_id: businessId, // 这个就是 session id
            dialogue_id: dialogueId,
            input_type: INPUT_TYPE.INPUT_BY_CLOSE_VIRTUAL_CALL,
            // message_text: buttonText,
            is_bypass: true,
            ...customCallback,
          });
          // saveDataToLocalStorage(VIRTUAL_CALL_NEED_RECOVER_HISTORY, 'false');
          chatbotCommonVariable?.UPDATE_VIRTUAL_CALL_NEED_RECOVER_HISTORY(false);
          window.bridgeUnRegisterHandler('viewWillReappear', bridgeCallback); // 取消 virtual call 页面 监听
        } else {
          // 如果触发 viewWillReappear 事件 通话又没有结束 说明是 vc 最小化
          // saveDataToLocalStorage(VIRTUAL_CALL_NEED_RECOVER_HISTORY, 'true');
          chatbotCommonVariable?.UPDATE_VIRTUAL_CALL_NEED_RECOVER_HISTORY(true);
        }
      }
    };
    window.bridgeRegisterHandler('viewWillReappear', bridgeCallback);
  });
};

export default inject(getChildInject('initStore', 'userStore', 'agentStore'))(observer(ChannelOptionButton));
